import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { Page } from '@/shared/ui/Page';
import { PageHeader } from '@/shared/ui/PageHeader';
import { GreenLiquidGlassButton } from '@/shared/ui/GreenLiquidGlassButton';
import { subscriptionApi } from '@/entities/subscription/api';
import { amountText } from '@/shared/lib/money';
import { cn } from '@/shared/lib/cn';
import { useT } from '@/shared/lib/i18n';

type Scenario = 'today' | 'from_period_start' | 'next_period' | 'specific_date';

export function ChangePrice() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const t = useT();
  const [price, setPrice] = useState('');
  const [scenario, setScenario] = useState<Scenario>('next_period');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));

  const detailQ = useQuery({
    queryKey: ['subscription', id],
    queryFn: () => subscriptionApi.getDetail(id!),
    enabled: !!id,
  });

  const sub = detailQ.data?.subscription;
  const parsedPrice = Number(price.replace(',', '.'));
  const canSubmit = Boolean(sub && Number.isFinite(parsedPrice) && parsedPrice > 0);
  const displayPrice = Number.isFinite(parsedPrice) && parsedPrice > 0
    ? parsedPrice
    : Number(sub?.total_price ?? 0);

  const effectiveDate = useMemo(() => {
    if (scenario === 'specific_date') return date;
    if (scenario === 'next_period') {
      const base = sub?.billing_date ? new Date(sub.billing_date) : new Date();
      base.setMonth(base.getMonth() + 1);
      return base.toISOString().slice(0, 10);
    }
    return new Date().toISOString().slice(0, 10);
  }, [date, scenario, sub?.billing_date]);

  const mutation = useMutation({
    mutationFn: () => subscriptionApi.changePrice(id!, {
      new_price: parsedPrice,
      scenario: scenario === 'today' || scenario === 'specific_date' ? 'from_period_start' : scenario,
      effective_date: effectiveDate,
    }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['subscription', id] });
      await qc.invalidateQueries({ queryKey: ['subscriptions', 'holder'] });
      navigate(`/subscriptions/${id}`);
    },
  });

  const scenarios: { value: Scenario; label: string }[] = [
    { value: 'today', label: t('management.scenario.today') },
    { value: 'from_period_start', label: t('management.scenario.currentPeriod') },
    { value: 'next_period', label: t('management.scenario.nextPeriod') },
    { value: 'specific_date', label: t('management.scenario.specificDate') },
  ];

  return (
    <Page>
      <PageHeader title={<span className="font-bold text-[19px]">{t('management.changePrice.title')}</span>} />
      <div className="px-[18px] pb-5 flex flex-col gap-4 min-h-0">
        <div className="rounded-[18px] p-5 bg-brand-green/10 border border-brand-green/30 text-center">
          <div className="flex items-center justify-center gap-3">
            <span className="text-[24px] font-mono text-text-muted line-through">
              {sub ? amountText(sub.total_price, sub.currency) : t('common.dash')}
            </span>
            <span className="text-brand-green text-[22px]">→</span>
            <span className="text-[34px] font-bold font-mono bg-brand-gradient bg-clip-text text-transparent">
              {amountText(displayPrice, sub?.currency ?? 'RUB')}
            </span>
          </div>
        </div>

        <label className="flex flex-col gap-2">
          <span className="text-[14px] font-semibold text-text-secondary">{t('management.changePrice.newPrice')}</span>
          <input
            inputMode="decimal"
            value={price}
            onChange={(event) => setPrice(event.target.value)}
            className="rounded-[14px] px-4 py-4 text-[24px] font-mono font-semibold bg-white/55 border border-white/65 outline-none focus:border-brand-green"
            placeholder="450"
          />
        </label>

        <div className="flex flex-col gap-2">
          <p className="text-[14px] font-semibold text-text-secondary">{t('management.changePrice.applyFrom')}</p>
          {scenarios.map((item) => (
            <button
              key={item.value}
              onClick={() => setScenario(item.value)}
              className={cn(
                'flex items-center gap-3 rounded-[12px] px-4 py-3 text-[14px] bg-white/50 border transition-colors',
                scenario === item.value ? 'border-2 border-brand-green bg-brand-green/10' : 'border-white/60',
              )}
            >
              <span className={cn(
                'w-[18px] h-[18px] rounded-full border-2',
                scenario === item.value ? 'border-brand-green bg-brand-green' : 'border-[#b9c4bb]',
              )} />
              {item.label}
            </button>
          ))}
        </div>

        {scenario === 'specific_date' && (
          <input
            type="date"
            value={date}
            onChange={(event) => setDate(event.target.value)}
            className="rounded-[12px] px-4 py-3 text-[15px] bg-white/55 border border-white/60"
          />
        )}

        <div className="rounded-[12px] p-3 text-[13px] text-status-warn bg-[rgba(230,103,53,0.13)] border border-[rgba(230,103,53,0.3)]">
          {t('management.changePrice.preview', { date: effectiveDate })}
        </div>

        <div className="mt-auto">
          {mutation.isError && (
            <p className="text-[13px] text-status-debt text-center mb-2">{t('management.changePrice.error')}</p>
          )}
          <GreenLiquidGlassButton
            disabled={!canSubmit}
            loading={mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {t('management.changePrice.submit')}
          </GreenLiquidGlassButton>
        </div>
      </div>
    </Page>
  );
}
